import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";
import { Form as BaseForm, Formik, FormikHelpers } from "formik";
import styled from "styled-components";

import { API } from "@api";
import { IAuctionStakeholder } from "@api/stakeholders";
import { IRequestTopic } from "@api/utils";
import {
  FileField,
  Icon,
  Loader,
  Modal,
  NumberField,
  SwitchField,
  Tabs,
  TextField,
} from "@components";
import { ButtonGroupField } from "@components/ButtonGroupField";
import { DateField } from "@components/DateField";
import DepositAttachmentField from "@components/DepositAttachmentField";
import { ErrorRenderer } from "@components/FormError";
import { Stakeholders } from "@components/forms/CreateAuctionForm/Stakeholders";
import { useGlobalContext } from "@contexts/GlobalContext";
import { useI18n } from "@i18n";
import { device } from "@styles/breakpoints";
import {
  Agency,
  AssetFamily,
  AuctionStatus,
  auctionTime,
  getCityName,
  IAuction,
  ICity,
  ICountry,
  IEmailLanguage,
  OptionalNotNull,
} from "@utils";
import { Button } from "../../Button";
import { Label } from "../../Label";
import { SelectField } from "../../SelectField";
import { TextAreaField } from "../../TextAreaField";
import {
  MAX_CONVERSION_RATE_VALUE,
  MIN_CONVERSION_RATE_VALUE,
  MIN_NON_WFP_LOTS_VALUE,
  MIN_UNHCR_SERVICES_VALUE,
} from "../CreateAuctionForm";
import { AuctioneerFeeByAssetField } from "../CreateAuctionForm/AuctioneerFeeByAssetField";
import { DepositByAssetField } from "../CreateAuctionForm/DepositByAssetField";
import { EmailField } from "../CreateAuctionForm/EmailField";
import { INITIAL_VALUES } from "./initialValues";
import { validateForm, VALIDATION_SCHEMA } from "./validationSchema";

export type TUpdateAuctionFormValues = Omit<
  typeof INITIAL_VALUES,
  "stakeholders"
> & {
  stakeholders?: typeof INITIAL_VALUES["stakeholders"];
};

type Props = {
  initialValues: IAuction;
  onSubmit: (
    values: TUpdateAuctionFormValues,
    helpers: FormikHelpers<TUpdateAuctionFormValues>
  ) => Promise<void>;
  loading?: boolean;
  onClose?: () => void;
};

const Form = styled(BaseForm)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;

  & > *:not(:last-child) {
    margin-right: 24px;
  }

  @media ${device.tablet} {
    flex-direction: column;

    & > *:not(:last-child) {
      margin-right: 0;
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  max-width: 300px;
`;

const SwitchContainer = styled.div`
  align-items: center;
  display: flex;
  text-transform: uppercase;
  justify-content: space-between;

  span {
    min-width: 280px;
  }

  div {
    margin-top: 0;
  }
`;

const SwitchGroup = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const RowWithLabel = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const LabelBox = styled.div`
  display: flex;
  align-items: center;
  padding-right: 15px;
  width: 200px;

  & ${Label} {
    margin-right: auto;
  }
`;

const TooltipContent = styled.div`
  max-width: 20rem;
`;

/** Match CreateAuctionForm tiny helpers (minimal new components) */
const FlexAlignCenterPR20 = styled.div`
  display: flex;
  align-items: center;
  padding-right: 20px;
`;

const AlignEndBox = styled.div`
  display: flex;
  align-items: flex-end;
`;

const LabelBoxPaddedBottom = styled(LabelBox)`
  padding-bottom: 6px;
`;

const LabelSpan = styled.span`
  text-transform: initial;
`;

const ErrorWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.tripleMargin};
`;

const DesktopLayout = styled.div`
  display: flex;

  @media (max-width: 960px) {
    display: none;
  }
`;

const MobileLayout = styled.div`
  display: none;

  @media (max-width: 960px) {
    display: block;
  }
`;

const TopicNameLabel = styled(Label)<{ bold?: boolean }>`
  font-size: 14px;
  text-transform: initial;
  font-weight: ${({ bold }) => (bold ? "bold" : "inherit")};
`;

const MobileSwitchContainer = styled.div`
  width: 100%;
  margin-bottom: 16px;
  margin-top: 16px;
`;

const CustomEmailsContainer = styled.div`
  margin-bottom: 24px;
`;

const CustomEmailsLabel = styled(Label)`
  font-size: 12px;
  text-transform: initial;
`;

const EmailFieldWrapper = styled.div`
  margin-top: 16px;
`;

const DesktopSwitchContainer = styled.div`
  width: 20%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const DesktopColumnHeader = styled.div`
  width: 20%;
  text-align: center;
`;

const DesktopEmailColumnHeader = styled.div`
  width: 40%;
  text-align: left;
`;

const DesktopTopicColumn = styled.div`
  width: 20%;
`;

const DesktopEmailColumn = styled.div`
  width: 40%;
`;

const TopicContainer = styled.div`
  margin-bottom: 16px;
`;

// Reusable Switch Field Component
interface MobileSwitchFieldProps {
  topicId: number;
  label: string;
  fieldName: string;
  setFieldValue: (field: string, value: any) => void;
}

const MobileSwitchField: React.FC<MobileSwitchFieldProps> = ({
  topicId,
  label,
  fieldName,
  setFieldValue,
}) => (
  <MobileSwitchContainer>
    <SwitchField
      label={label}
      labelStyles={{ fontSize: "12px", textTransform: "initial" }}
      horizontal={true}
      name={`requestTopics.${topicId}.${fieldName}`}
      onChange={(checked) => {
        setFieldValue(`requestTopics.${topicId}.topic`, topicId);
        setFieldValue(`requestTopics.${topicId}.${fieldName}`, checked);
      }}
    />
  </MobileSwitchContainer>
);

export const UpdateAuctionForm: FC<Props> = ({
  loading,
  initialValues,
  onSubmit,
  onClose,
}) => {
  const {
    waffles: {
      switches: { stakeholder },
    },
  } = useGlobalContext();

  const { t } = useI18n();
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [cities, setCities] = useState<ICity[]>([]);
  const [countries, setCountries] = useState<ICountry[]>([]);
  const [stakeholders, setStakeholders] = useState<Array<IAuctionStakeholder>>(
    []
  );
  const [currencies, setCurrencies] = useState<{ code: string }[]>([]);
  const [emailLanguages, setEmailLanguages] = useState<IEmailLanguage[]>([]);
  const [conversionRateDisabled, setConversionRateDisabled] = useState(
    initialValues.currency.code === "USD"
  );
  const [auctionImage, setAuctionImage] = useState<string>("");
  const [uploadedPhoto, setUploadedPhoto] = useState<string>("");
  const [imageIsLoading, setImageIsLoading] = useState<boolean>(false);
  const [timezones, setTimezones] = useState<Array<any>>([]);
  const [gasStrategies, setGasStrategies] = useState<Array<any>>([]);
  const [requestTopics, setRequestTopics] = useState<IRequestTopic[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [customDepositFileUrl, setCustomDepositFileUrl] = useState<string>("");

  useEffect(() => {
    const getCountries = async () => {
      try {
        const countries = await API.users.countries();
        setCountries(countries);
      } catch (_) {
        setCountries([]);
      }
    };

    const getStakeholders = async (id: number) => {
      try {
        const stakeholders = await API.stakeholders.getAuctionStakeholders(id);
        const result = stakeholders.results.map((item) => ({
          country_geoname_id: item.country.toString(),
          agency: item.agency,
        }));
        setStakeholders(result);
      } catch (_) {
        setStakeholders([]);
      }
    };

    const getCurrencies = async () => {
      try {
        const currencies = await API.utils.currencies();
        setCurrencies(currencies);
      } catch (_) {
        setCurrencies([]);
      }
    };

    const getEmailLanguages = async () => {
      try {
        const languages = await API.auctions.getEmailLanguages();
        setEmailLanguages(languages);
      } catch (_) {
        setEmailLanguages([]);
      }
    };

    const getTimezones = async () => {
      try {
        const timezones = await API.utils.getTimezones();
        setTimezones(timezones);
      } catch (_) {
        setTimezones([]);
      }
    };

    const getAuctionImage = async (id: number) => {
      setImageIsLoading(true);
      try {
        const image = await API.auctions.getAuctionImage(id);
        const dataTransfer = new DataTransfer();
        const blob = await fetch(image.data.presigned_url).then((r) =>
          r.blob()
        );
        const metadata = { type: "image" };
        const file = new File([blob], "AuctionImage", metadata);
        dataTransfer.items.add(file);
        setAuctionImage(image.data.presigned_url);
        setImageIsLoading(false);
      } catch (e) {
        console.error("Get auction image error", e);
        setImageIsLoading(false);
      }
    };

    const getAuctionFile = async (id: number) => {
      setImageIsLoading(true);
      try {
        const data = await API.auctions.getAuctionCustomDepositFile(id);
        setCustomDepositFileUrl(data.data.presigned_url);
      } catch (e) {
        console.error("Get auction image error", e);
        setImageIsLoading(false);
      }
    };

    const getGasStrategies = async () => {
      try {
        const strategies = await API.utils.getGasStrategies();
        setGasStrategies(strategies.results);
      } catch (_) {
        setGasStrategies([]);
      }
    };

    const getRequestTopics = async () => {
      try {
        const topics = await API.utils.getRequestTopics();
        setRequestTopics(topics.results);
      } catch (_) {
        setRequestTopics([]);
      }
    };

    (async () => {
      const requests: Promise<unknown>[] = [
        getCountries(),
        getStakeholders(initialValues.id),
        getCurrencies(),
        getEmailLanguages(),
        getTimezones(),
        getGasStrategies(),
        getRequestTopics(),
      ];
      if (initialValues.enable_deposit_custom_payment) {
        requests.push(getAuctionFile(initialValues.id));
      }

      if (initialValues.auction_image) {
        requests.push(getAuctionImage(initialValues.id));
      }
      await Promise.all(requests);
      setInitialLoading(false);
    })();
  }, []);

  async function getCitiesByCountry(countryId: number) {
    try {
      const cities = await API.users.citiesByCountry(countryId);
      setCities(cities);
    } catch (_) {
      setCities([]);
    }
  }

  useEffect(() => {
    if (initialValues.location) {
      (async () => {
        await getCitiesByCountry(initialValues.location.country.geoname_id);
      })();
    }
  }, [countries, initialValues]);

  const transformRequestTopicsData = (values: any) => {
    if (!requestTopics || !requestTopics.length) return [];

    return requestTopics.map((topic) => {
      const topicData = values.requestTopics?.[topic.id];
      return {
        topic: topic.id,
        bcc_gas_representative: topicData?.bcc_gas_representative || false,
        bcc_co_focal_point: topicData?.bcc_co_focal_point || false,
        additional_emails: topicData?.additional_emails || [],
      };
    });
  };

  const handleSubmit = useCallback(
    async (values, helpers) => {
      let valuesToUpdate = values;
      // Transform request topics data
      const requestTopics = transformRequestTopicsData(values);

      if (
        initialValues.status === AuctionStatus.DONE ||
        initialValues.status === AuctionStatus.LIVE
      ) {
        valuesToUpdate = {
          conversionRate: values.conversionRate,
          unhcrServicesFee: values.unhcrServicesFee,
          nonWfpLots: values.nonWfpLots,
          requestTopics,
          ...(values.auctionImage?.name && {
            auctionImage: values.auctionImage,
          }),
        };
      } else {
        valuesToUpdate = {
          ...valuesToUpdate,
          requestTopics,
        };
      }

      helpers.setSubmitting(true);
      await onSubmit(valuesToUpdate, helpers);
      helpers.setSubmitting(false);
    },
    [onSubmit, initialValues.status, requestTopics]
  );

  useEffect(() => {
    createUrl(uploadedPhoto);
  }, [uploadedPhoto]);

  const handleChange = async (countryId: number) => {
    getCitiesByCountry(countryId);
  };

  const createUrl = (files: any): void => {
    const url: string[] = [];
    for (const file in files) {
      typeof files[file] === "object" &&
        url.push(URL.createObjectURL(files[file]));
    }
    setAuctionImage(url[0]);
  };

  const handlePhotoField = (photo: any, setFieldValue: any) => {
    setFieldValue("auctionImage", photo[0]);
    setUploadedPhoto(photo);
  };

  const isDisabled = useMemo(
    () =>
      initialValues.status === AuctionStatus.DONE ||
      initialValues.status === AuctionStatus.LIVE,
    [initialValues.status]
  );

  const formInitialValues: TUpdateAuctionFormValues = useMemo(() => {
    const deposits = Object.entries(initialValues.deposits ?? {}).reduce(
      (res, [asset, deposit]) => {
        const existingAssetGroupIndex = res.findIndex(
          (assetGroup) => assetGroup.deposit === deposit
        );
        if (existingAssetGroupIndex !== -1) {
          res[existingAssetGroupIndex].assets.push(asset as AssetFamily);
        } else {
          res.push({ assets: [asset as AssetFamily], deposit });
        }
        return res;
      },
      [] as {
        assets: Array<AssetFamily>;
        deposit: OptionalNotNull<number>;
      }[]
    );

    const auctioneerFees = Object.entries(
      initialValues.auctioneer_fees ?? {}
    ).reduce(
      (res, [asset, fee]) => {
        const existingAssetGroupIndex = res.findIndex(
          (assetGroup) => assetGroup.fee === fee
        );
        if (existingAssetGroupIndex !== -1) {
          res[existingAssetGroupIndex].assets.push(asset as AssetFamily);
        } else {
          res.push({ assets: [asset as AssetFamily], fee });
        }
        return res;
      },
      [] as {
        assets: Array<AssetFamily>;
        fee: OptionalNotNull<number>;
      }[]
    );

    return {
      ...INITIAL_VALUES,
      agency: initialValues.agency,
      city: initialValues.location.geoname_id.toString(),
      country: initialValues.location.country.geoname_id.toString(),
      currency: initialValues.currency.code,
      conversionRate: initialValues.conversion_rate ?? "",
      requiresOnlineDeposits: initialValues.requires_online_deposits ?? false,
      // map custom deposit related fields if API provides them
      customDeposit: initialValues.enable_deposit_custom_payment ?? false,
      customDepositName: initialValues.custom_deposit_name ?? "",
      depositAttachment: undefined as any,
      custom_deposit_file: initialValues.custom_deposit_file,
      date: initialValues.auction_date,
      auctionTime: initialValues.auction_time,
      auctionEndTime: initialValues.auction_end_time,
      auctionEndDate: initialValues.auction_end_date,
      depositDate: initialValues.deposits_end_date ?? "",
      depositTime: initialValues.deposits_end_time ?? "",
      stakeholders: stakeholders,
      timezone: initialValues.timezone ?? "",
      auctionImage: initialValues.auction_image ?? "",
      isOnline: initialValues.is_online,
      deposits,
      auctioneerFees,
      gasStrategy: initialValues.gas_strategy,
      nonWfpLots: initialValues.unhcr_lot_count ?? undefined,
      unhcrServicesFee: initialValues.unhcr_services ?? undefined,
      auctioneerInvoice: initialValues.auctioneer_invoice ?? undefined,
      focalPoint: initialValues.co_focal_point
        ? {
            email: initialValues.co_focal_point.email,
            fullName: initialValues.co_focal_point.full_name,
            phone: initialValues.co_focal_point.phone,
          }
        : undefined,
      paymentAccount: initialValues.payment_account ?? "",
      paymentConditions: initialValues.payment_conditions ?? "",
      paymentConditionsFr: initialValues.payment_conditions_fr ?? "",
      paymentConditionsEs: initialValues.payment_conditions_es ?? "",
      paymentConditionsPt: initialValues.payment_conditions_pt ?? "",
      pickupDetails: initialValues.pickup_details ?? "",
      clearanceInstructions: initialValues.clearance_instructions ?? "",
      representative: initialValues.gas_team_representative
        ? {
            email: initialValues.gas_team_representative.email,
            fullName: initialValues.gas_team_representative.full_name,
            phone: initialValues.gas_team_representative.phone,
          }
        : undefined,
      sealedBid: initialValues.sealed,
      supervisor: initialValues.unhcr_supervisor
        ? {
            email: initialValues.unhcr_supervisor.email,
            fullName: initialValues.unhcr_supervisor.full_name,
            phone: initialValues.unhcr_supervisor.phone,
          }
        : undefined,
      sendEmailBidWasOutbid: initialValues.send_email_bid_was_outbid,
      sendEmailBidderWon: initialValues.send_email_bidder_won,
      emailLanguage: initialValues.email_language,
      enableBankTransfer: initialValues.enable_bank_transfer,
      enableOnlinePayment: initialValues.enable_online_payment,
      enableDepositBankTransfer: initialValues.enable_deposit_bank_transfer,
      enableDepositOnlinePayment: initialValues.enable_deposit_online_payment,
    };
  }, [initialValues, stakeholders]);

  return (
    <Formik<TUpdateAuctionFormValues>
      initialValues={formInitialValues}
      validationSchema={VALIDATION_SCHEMA(initialValues.status)}
      validate={validateForm}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        isValid,
        values,
        handleSubmit,
        handleChange: onChange,
        setFieldValue,
        errors,
      }) => {
        // Initialize request topics form values when requestTopics are loaded
        useEffect(() => {
          if (requestTopics && requestTopics.length > 0 && !initialLoading) {
            const initialTopicValues: Record<string, any> =
              requestTopics.reduce((acc, topic) => {
                // Check if there's existing data for this topic from the auction
                const existingTopicData =
                  initialValues.topic_bcc_settings?.find(
                    (setting) => setting.topic === topic.id
                  );

                acc[`requestTopics.${topic.id}.topic`] = topic.id;
                acc[`requestTopics.${topic.id}.bcc_gas_representative`] =
                  existingTopicData?.bcc_gas_representative || false;
                acc[`requestTopics.${topic.id}.bcc_co_focal_point`] =
                  existingTopicData?.bcc_co_focal_point || false;
                acc[`requestTopics.${topic.id}.additional_emails`] =
                  existingTopicData?.additional_emails || [];
                return acc;
              }, {} as Record<string, any>);

            // Set initial values for request topics
            Object.keys(initialTopicValues).forEach((key) => {
              setFieldValue(key, initialTopicValues[key]);
            });
          }
        }, [
          requestTopics,
          initialLoading,
          setFieldValue,
          initialValues.topic_bcc_settings,
        ]);

        // Custom validation for custom deposit requirements
        const isCustomDepositValid =
          !values.customDeposit ||
          (values.customDepositName &&
            values.customDepositName.trim() &&
            (values.depositAttachment || customDepositFileUrl));

        const isFormValid = isValid && isCustomDepositValid;

        return (
          <Form onSubmit={handleSubmit}>
            <Modal
              width="100%"
              primaryActions={<></>}
              actions={
                <>
                  <Button onClick={() => onClose?.()} bordered>
                    {t<string>("auctionForm.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={!isFormValid}
                    onClick={() => null}
                    loading={loading}
                  >
                    {t<string>("auctionForm.update")}
                  </Button>
                </>
              }
              onClose={() => null}
              fixed
            >
              {initialLoading && <Loader size="big" full />}
              <Label>{t("auctionForm.location")}</Label>
              <Row>
                <SelectField
                  name="country"
                  placeholder={t("auctionForm.country")}
                  options={countries.map((country) => ({
                    label: country.name,
                    value: country.geoname_id,
                  }))}
                  onChange={(event) => {
                    setFieldValue("city", "");
                    onChange(event);
                    handleChange(Number(event.target.value));
                  }}
                  disabled={isDisabled}
                />
                <SelectField
                  name="city"
                  placeholder={t("auctionForm.city")}
                  options={cities.map((city) => ({
                    label: getCityName(city),
                    value: city.geoname_id,
                  }))}
                  disabled={
                    !Boolean(values.country) ||
                    cities.length === 0 ||
                    isDisabled
                  }
                />
              </Row>

              <Row>
                <DateField
                  name="date"
                  placeholder={t("auctionForm.date.placeholder")}
                  label={t("auctionForm.date.label")}
                  disabled={isDisabled}
                />
                {!initialValues.is_online ? (
                  <SelectField
                    name="currency"
                    placeholder={t("auctionForm.currency.placeholder")}
                    label={t("auctionForm.currency.label")}
                    options={currencies.map((currency) => ({
                      label: currency.code,
                      value: currency.code,
                    }))}
                    onChange={(event) => {
                      onChange(event);
                      const currency = event.target.value;
                      if (currency === "USD") {
                        setConversionRateDisabled(true);
                        setFieldValue("conversionRate", 1);
                      } else {
                        setConversionRateDisabled(false);
                        setFieldValue("conversionRate", "");
                      }
                    }}
                    disabled={isDisabled}
                  />
                ) : (
                  <SelectField
                    name="auctionTime"
                    placeholder={String(
                      t("auctionForm.auctionTime.placeholder")
                    )}
                    label={String(t("auctionForm.auctionTime.label"))}
                    options={auctionTime}
                  />
                )}
              </Row>

              {initialValues.is_online && (
                <>
                  <Row>
                    <DateField
                      name="auctionEndDate"
                      placeholder={t("auctionForm.auctionEndDate.placeholder")}
                      label={t("auctionForm.auctionEndDate.label")}
                    />
                    <SelectField
                      name="auctionEndTime"
                      label={String(t("auctionForm.auctionEndTime.label"))}
                      placeholder={String(
                        t("auctionForm.auctionEndTime.placeholder")
                      )}
                      options={auctionTime}
                    />
                  </Row>
                  {stakeholder ? <Stakeholders countries={countries} /> : null}
                  <Row>
                    <SelectField
                      name="timezone"
                      placeholder={String(
                        t("auctionForm.timezone.placeholder")
                      )}
                      label={String(t("auctionForm.timezone.label"))}
                      options={timezones.map((zone) => ({
                        label: zone.display_name,
                        value: zone.value,
                      }))}
                    />
                  </Row>
                  <Row>
                    <SelectField
                      name="currency"
                      placeholder={t("auctionForm.currency.placeholder")}
                      label={t("auctionForm.currency.label")}
                      options={currencies.map((currency) => ({
                        label: currency.code,
                        value: currency.code,
                      }))}
                      onChange={(event) => {
                        onChange(event);
                        const currency = event.target.value;
                        if (currency === "USD") {
                          setConversionRateDisabled(true);
                          setFieldValue("conversionRate", 1);
                        } else {
                          setConversionRateDisabled(false);
                          setFieldValue("conversionRate", "");
                        }
                      }}
                    />
                  </Row>
                </>
              )}

              {!initialValues.is_online && stakeholder ? (
                <Stakeholders countries={countries} />
              ) : null}

              <Row>
                <NumberField
                  name="conversionRate"
                  label={t("auctionForm.conversionRate.label")}
                  placeholder={t("auctionForm.conversionRate.placeholder")}
                  min={MIN_CONVERSION_RATE_VALUE}
                  max={MAX_CONVERSION_RATE_VALUE}
                  step="0.001"
                  disabled={conversionRateDisabled}
                />
              </Row>

              {initialValues.is_online && (
                <>
                  <Row>
                    <SwitchContainer>
                      <FlexAlignCenterPR20>
                        <Label className="mr-6">
                          {t("auctionForm.depositToggle")}
                        </Label>
                      </FlexAlignCenterPR20>
                      <AlignEndBox>
                        <SwitchField
                          name="requiresOnlineDeposits"
                          disabled={
                            initialValues.status === AuctionStatus.DONE ||
                            initialValues.status === AuctionStatus.LIVE
                          }
                        />
                      </AlignEndBox>
                    </SwitchContainer>
                  </Row>

                  {values.requiresOnlineDeposits && (
                    <>
                      <DepositByAssetField />

                      <Row>
                        <SwitchGroup>
                          <SwitchContainer>
                            <FlexAlignCenterPR20>
                              <Label className="mr-6">
                                {t("auctionForm.enableDepositBankTransfer")}
                              </Label>
                            </FlexAlignCenterPR20>
                            <AlignEndBox>
                              <SwitchField name="enableDepositBankTransfer" />
                            </AlignEndBox>
                          </SwitchContainer>

                          <SwitchContainer>
                            <FlexAlignCenterPR20>
                              <Label className="mr-6">
                                {t("auctionForm.enableDepositOnlinePayment")}
                              </Label>
                            </FlexAlignCenterPR20>
                            <AlignEndBox>
                              <SwitchField name="enableDepositOnlinePayment" />
                            </AlignEndBox>
                          </SwitchContainer>

                          <SwitchContainer>
                            <FlexAlignCenterPR20>
                              <Label className="mr-6">
                                {t("auctionForm.custom")}
                              </Label>
                            </FlexAlignCenterPR20>
                            <AlignEndBox>
                              <SwitchField name="customDeposit" />
                            </AlignEndBox>
                          </SwitchContainer>
                        </SwitchGroup>
                      </Row>

                      {values.customDeposit && (
                        <>
                          <Label>
                            {t("auctionForm.customDepositName")}{" "}
                            <LabelSpan>
                              {t("auctionForm.customDepositNote")}
                            </LabelSpan>
                          </Label>
                          <TextField
                            name="customDepositName"
                            placeholder={t("auctionForm.customDepositName")}
                          />
                          {errors?.customDepositName && (
                            <ErrorWrapper>
                              <ErrorRenderer children="auctionForm.errors.customDepositName.required" />
                            </ErrorWrapper>
                          )}

                          <DepositAttachmentField
                            name="depositAttachment"
                            disabled={!values.requiresOnlineDeposits}
                            required
                            customDepositFileUrl={customDepositFileUrl}
                            onRemoveFile={() => setCustomDepositFileUrl("")}
                          />
                        </>
                      )}

                      <Row>
                        <DateField
                          name="depositDate"
                          disabled={!values.deposits.length}
                          placeholder={t("auctionForm.depositDate.placeholder")}
                          label={t("auctionForm.depositDate.label")}
                        />
                        <SelectField
                          name="depositTime"
                          disabled={Object.keys(values.deposits).length === 0}
                          label={String(t("auctionForm.depositTime.label"))}
                          placeholder={String(
                            t("auctionForm.depositTime.placeholder")
                          )}
                          options={auctionTime}
                        />
                      </Row>
                    </>
                  )}

                  <Label>{t("auctionForm.gasStrategy")}</Label>
                  <Row>
                    <SelectField
                      name="gasStrategy"
                      placeholder="Default"
                      options={gasStrategies.map((strategy) => ({
                        label: strategy.name,
                        value: strategy.id,
                      }))}
                    />
                  </Row>

                  <Row>
                    <SwitchContainer>
                      <FlexAlignCenterPR20>
                        <Label className="mr-6">
                          {t("auctionForm.emailNotification.outbid")}
                        </Label>
                        <div id="outbid-tooltip">
                          <Icon.InfoCircle />
                        </div>
                      </FlexAlignCenterPR20>
                      <AlignEndBox>
                        <SwitchField name="sendEmailBidWasOutbid" />
                      </AlignEndBox>
                    </SwitchContainer>
                    <Tooltip anchorId="outbid-tooltip">
                      <TooltipContent>
                        {t("auctionForm.emailNotificationTooltip.outbid")}
                      </TooltipContent>
                    </Tooltip>
                  </Row>

                  <Row>
                    <SelectField
                      name="emailLanguage"
                      placeholder={t("auctionForm.emailLanguage")}
                      options={emailLanguages.map((language) => ({
                        label: language.name,
                        value: language.code,
                      }))}
                    />
                  </Row>

                  <Row>
                    <SwitchGroup>
                      <SwitchContainer>
                        <FlexAlignCenterPR20>
                          <Label className="mr-6">
                            {t("auctionForm.enableBankTransfer")}
                          </Label>
                        </FlexAlignCenterPR20>
                        <AlignEndBox>
                          <SwitchField name="enableBankTransfer" />
                        </AlignEndBox>
                      </SwitchContainer>

                      <SwitchContainer>
                        <FlexAlignCenterPR20>
                          <Label className="mr-6">
                            {t("auctionForm.enableOnlinePayment")}
                          </Label>
                        </FlexAlignCenterPR20>
                        <AlignEndBox>
                          <SwitchField name="enableOnlinePayment" />
                        </AlignEndBox>
                      </SwitchContainer>
                    </SwitchGroup>
                  </Row>
                </>
              )}

              <Row>
                <RowWithLabel>
                  <LabelBox>
                    <Label className="mr-6">
                      {t("auctionForm.paymentAccount")}
                    </Label>
                    <div id="payment-account-tooltip">
                      <Icon.InfoCircle />
                    </div>
                  </LabelBox>
                  <TextAreaField
                    name="paymentAccount"
                    placeholder={t("auctionForm.paymentAccount")}
                    disabled={isDisabled}
                  />
                </RowWithLabel>
                <Tooltip anchorId="payment-account-tooltip">
                  <TooltipContent>
                    {t("auctionForm.paymentAccountTooltip")}
                  </TooltipContent>
                </Tooltip>
              </Row>

              <Row>
                <RowWithLabel>
                  <LabelBoxPaddedBottom>
                    <Label className="mr-6">
                      {t("auctionForm.paymentConditions")}
                    </Label>
                    <div id="payment-conditions-tooltip">
                      <Icon.InfoCircle />
                    </div>
                  </LabelBoxPaddedBottom>
                  <Tabs
                    onTabClick={(index) => setActiveTabIndex(index)}
                    headers={[
                      String(t("auctionForm.languages.english")),
                      String(t("auctionForm.languages.french")),
                      String(t("auctionForm.languages.portuguese")),
                      String(t("auctionForm.languages.spanish")),
                    ]}
                  >
                    <TextAreaField
                      name="paymentConditions"
                      placeholder={t("auctionForm.paymentConditions")}
                      disabled={isDisabled}
                    />
                    <TextAreaField
                      name="paymentConditionsFr"
                      placeholder={t("auctionForm.paymentConditions")}
                      disabled={isDisabled}
                    />
                    <TextAreaField
                      name="paymentConditionsPt"
                      placeholder={t("auctionForm.paymentConditions")}
                      disabled={isDisabled}
                    />
                    <TextAreaField
                      name="paymentConditionsEs"
                      placeholder={t("auctionForm.paymentConditions")}
                      disabled={isDisabled}
                    />
                  </Tabs>
                </RowWithLabel>
                <Tooltip anchorId="payment-conditions-tooltip">
                  <TooltipContent>
                    {t("auctionForm.paymentConditionsTooltip")}
                  </TooltipContent>
                </Tooltip>
              </Row>

              {initialValues.is_online && (
                <>
                  <Row>
                    <RowWithLabel>
                      <LabelBox>
                        <Label className="mr-6">
                          {t("auctionForm.pickupDetails")}
                        </Label>
                        <div id="pickup-details-tooltip">
                          <Icon.InfoCircle />
                        </div>
                      </LabelBox>
                      <TextAreaField name="pickupDetails" />
                    </RowWithLabel>
                    <Tooltip anchorId="pickup-details-tooltip">
                      <TooltipContent>
                        {t("auctionForm.pickupDetailsTooltip")}
                      </TooltipContent>
                    </Tooltip>
                  </Row>
                  <Row>
                    <RowWithLabel>
                      <LabelBox>
                        <Label className="mr-6">
                          {t("auctionForm.clearanceInstructions")}
                        </Label>
                        <div id="clearance-instructions-tooltip">
                          <Icon.InfoCircle />
                        </div>
                      </LabelBox>
                      <TextAreaField name="clearanceInstructions" />
                    </RowWithLabel>
                    <Tooltip anchorId="clearance-instructions-tooltip">
                      <TooltipContent>
                        {t("auctionForm.clearanceInstructionsTooltip")}
                      </TooltipContent>
                    </Tooltip>
                  </Row>
                </>
              )}

              {!initialValues.is_online && (
                <>
                  <AuctioneerFeeByAssetField disabled={isDisabled} />
                  <DepositByAssetField />
                  <Label>{t("auctionForm.gasStrategy")}</Label>
                  <Row>
                    <SelectField
                      name="gasStrategy"
                      placeholder="Default"
                      options={gasStrategies.map((strategy) => ({
                        label: strategy.name,
                        value: strategy.id,
                      }))}
                    />
                  </Row>
                  <Row>
                    <ButtonGroupField
                      name="agency"
                      label={t("auctionForm.agency")}
                      options={[
                        {
                          label: String(t("auctionForm.agencies.unhcr")),
                          value: Agency.UNHCR,
                        },
                        {
                          label: String(t("auctionForm.agencies.wfp")),
                          value: Agency.WFP,
                        },
                      ]}
                      onChange={(agency: Agency) => {
                        if (agency === Agency.WFP) {
                          setFieldValue(
                            "supervisor",
                            INITIAL_VALUES.supervisor
                          );
                          setFieldValue(
                            "nonWfpLots",
                            INITIAL_VALUES.nonWfpLots
                          );
                          setFieldValue(
                            "unhcrServicesFee",
                            INITIAL_VALUES.unhcrServicesFee
                          );
                        }
                      }}
                      disabled={isDisabled}
                    />
                    <SwitchField
                      name="sealedBid"
                      label={
                        values.sealedBid
                          ? t("auctionForm.sealedBid")
                          : t("auctionForm.public")
                      }
                      disabled={isDisabled}
                    />
                  </Row>
                </>
              )}

              {values.agency === Agency.UNHCR && !initialValues.is_online && (
                <>
                  <Row>
                    <NumberField
                      name="nonWfpLots"
                      label={t("auctionForm.nonWfpLots.label")}
                      placeholder={t("auctionForm.nonWfpLots.placeholder")}
                      step={1}
                      min={MIN_NON_WFP_LOTS_VALUE}
                    />
                    <NumberField
                      name="unhcrServicesFee"
                      label={
                        values.currency
                          ? t("auctionForm.unhcrServicesFee.label", {
                              currency: "(" + values.currency + ")",
                            })
                          : t("auctionForm.unhcrServicesFee.label")
                      }
                      placeholder={
                        values.currency
                          ? t("auctionForm.unhcrServicesFee.placeholder", {
                              currency: "(" + values.currency + ")",
                            })
                          : t("auctionForm.unhcrServicesFee.placeholder")
                      }
                      step={0.01}
                      min={MIN_UNHCR_SERVICES_VALUE}
                    />
                  </Row>
                  <Label>{t("auctionForm.supervisor")}</Label>
                  <Row>
                    <TextField
                      name="supervisor.fullName"
                      placeholder={t("auctionForm.fullName")}
                      disabled={isDisabled}
                    />
                    <TextField
                      name="supervisor.email"
                      placeholder={t("auctionForm.email")}
                      disabled={isDisabled}
                    />
                  </Row>
                  <Row>
                    <TextField
                      name="supervisor.phone"
                      placeholder={t("auctionForm.phone")}
                      disabled={isDisabled}
                    />
                  </Row>
                </>
              )}

              {values.agency === Agency.WFP && !values.isOnline && (
                <Row>
                  <NumberField
                    name="auctioneerInvoice"
                    label={
                      values.currency
                        ? t("auctionForm.auctioneerInvoice.label", {
                            currency: "(" + values.currency + ")",
                          })
                        : t("auctionForm.auctioneerInvoice.label")
                    }
                    placeholder={
                      values.currency
                        ? t("auctionForm.auctioneerInvoice.label", {
                            currency: "(" + values.currency + ")",
                          })
                        : t("auctionForm.auctioneerInvoice.label")
                    }
                    step={0.01}
                    min={MIN_UNHCR_SERVICES_VALUE}
                  />
                </Row>
              )}

              <Label>{t("auctionForm.focalPoint")}</Label>
              <Row>
                <TextField
                  name="focalPoint.fullName"
                  placeholder={t("auctionForm.fullName")}
                  disabled={isDisabled}
                />
                <TextField
                  name="focalPoint.email"
                  placeholder={t("auctionForm.email")}
                  disabled={isDisabled}
                />
              </Row>
              <Row>
                <TextField
                  name="focalPoint.phone"
                  placeholder={t("auctionForm.phone")}
                  disabled={isDisabled}
                />
              </Row>

              <Label>{t("auctionForm.representative")}</Label>
              <Row>
                <TextField
                  name="representative.fullName"
                  placeholder={t("auctionForm.fullName")}
                  disabled={isDisabled}
                />
                <TextField
                  name="representative.email"
                  placeholder={t("auctionForm.email")}
                  disabled={isDisabled}
                />
              </Row>
              <Row>
                <TextField
                  name="representative.phone"
                  placeholder={t("auctionForm.phone")}
                  disabled={isDisabled}
                />
              </Row>

              {initialValues.is_online && (
                <Row>
                  <FileField
                    name="auctionImage"
                    accept="image/*"
                    label={t("lotForm.photo.label")}
                    placeholder={t("lotForm.photo.placeholder")}
                    onChange={(event) => {
                      handlePhotoField(
                        event.currentTarget.files,
                        setFieldValue
                      );
                    }}
                  />
                </Row>
              )}
              {initialValues.is_online &&
                values.auctionImage &&
                (imageIsLoading ? (
                  <Loader size="small" />
                ) : (
                  <ImageContainer>
                    <img
                      src={auctionImage}
                      alt="auction logo"
                      style={{ maxWidth: "300px" }}
                    />
                  </ImageContainer>
                ))}
              {initialValues.is_online &&
                requestTopics &&
                requestTopics.length > 0 &&
                !initialLoading && (
                  <>
                    <Label style={{ marginBottom: "16px" }}>
                      {t("auctionForm.communicationFocalPoints")}
                    </Label>

                    {/* Column Headers - Desktop Only */}
                    <DesktopLayout>
                      <Row style={{ width: "100%" }}>
                        <DesktopTopicColumn></DesktopTopicColumn>
                        <DesktopColumnHeader>
                          <TopicNameLabel>
                            {t("auctionForm.bccGasTeamRepresentative")}
                          </TopicNameLabel>
                        </DesktopColumnHeader>
                        <DesktopColumnHeader>
                          <TopicNameLabel>
                            {t("auctionForm.bccCoFocalPoint")}
                          </TopicNameLabel>
                        </DesktopColumnHeader>
                        <DesktopEmailColumnHeader>
                          <TopicNameLabel>
                            {t("auctionForm.customEmails")}
                          </TopicNameLabel>
                        </DesktopEmailColumnHeader>
                      </Row>
                    </DesktopLayout>

                    {/* Topic Rows */}
                    {requestTopics.map((topic) => (
                      <TopicContainer key={topic.id}>
                        {/* Desktop Layout */}
                        <DesktopLayout>
                          <Row
                            style={{
                              marginBottom: "0",
                              alignItems: "flex-start",
                              width: "100%",
                            }}
                          >
                            <DesktopTopicColumn>
                              <TopicNameLabel>{topic.name}</TopicNameLabel>
                            </DesktopTopicColumn>
                            <DesktopSwitchContainer>
                              <SwitchField
                                name={`requestTopics.${topic.id}.bcc_gas_representative`}
                                center={true}
                                onChange={(checked) => {
                                  setFieldValue(
                                    `requestTopics.${topic.id}.topic`,
                                    topic.id
                                  );
                                  setFieldValue(
                                    `requestTopics.${topic.id}.bcc_gas_representative`,
                                    checked
                                  );
                                }}
                              />
                            </DesktopSwitchContainer>
                            <DesktopSwitchContainer>
                              <SwitchField
                                name={`requestTopics.${topic.id}.bcc_co_focal_point`}
                                center={true}
                                onChange={(checked) => {
                                  setFieldValue(
                                    `requestTopics.${topic.id}.topic`,
                                    topic.id
                                  );
                                  setFieldValue(
                                    `requestTopics.${topic.id}.bcc_co_focal_point`,
                                    checked
                                  );
                                }}
                              />
                            </DesktopSwitchContainer>
                            <DesktopEmailColumn>
                              <EmailField
                                topicId={topic.id}
                                topicName={topic.name}
                              />
                            </DesktopEmailColumn>
                          </Row>
                        </DesktopLayout>

                        {/* Mobile Layout */}
                        <MobileLayout>
                          <TopicNameLabel bold>{topic.name}</TopicNameLabel>

                          {/* Neat Switch Layout for Mobile */}
                          <MobileSwitchField
                            topicId={topic.id}
                            label={t("auctionForm.bccGasTeamRepresentative")}
                            fieldName="bcc_gas_representative"
                            setFieldValue={setFieldValue}
                          />

                          <MobileSwitchField
                            topicId={topic.id}
                            label={t("auctionForm.bccCoFocalPoint")}
                            fieldName="bcc_co_focal_point"
                            setFieldValue={setFieldValue}
                          />

                          {/* Custom Emails Section for Mobile */}
                          <CustomEmailsContainer>
                            <CustomEmailsLabel>
                              {t("auctionForm.customEmails")}
                            </CustomEmailsLabel>
                            <EmailFieldWrapper>
                              <EmailField
                                topicId={topic.id}
                                topicName={topic.name}
                              />
                            </EmailFieldWrapper>
                          </CustomEmailsContainer>
                        </MobileLayout>
                      </TopicContainer>
                    ))}
                  </>
                )}
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};
